import { useEffect, useMemo, useState } from "react";
import { Form } from "reactstrap";
import Modal from "react-bootstrap/Modal";
import { APIMS } from "../../../../../../../../config/apims";
import { APISD } from "../../../../../../../../config/apisd";
import "bulma/css/bulma.css";
import "../../../../../../../../index.css";
import ToastError from "../../../../../../../NotificationToast/ToastError";
import ToastSuccess from "../../../../../../../NotificationToast/ToastSuccess";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

const css = `
  .ekskul-update-class-panel {
    margin-top: 8px;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    padding: 15px;
    background: #f8fafc;
  }

  .ekskul-update-class-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
  }

  .ekskul-update-class-toolbar strong {
    color: #1e293b;
    font-size: 13px;
  }

  .ekskul-update-class-toolbar span {
    color: #64748b;
    font-size: 12px;
  }

  .ekskul-update-class-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
  }

  .ekskul-update-class-item {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    min-height: 44px;
    padding: 10px 12px;
    border: 1px solid #e2e8f0;
    border-radius: 11px;
    background: #ffffff;
    color: #475569;
    font-size: 13px;
    line-height: 1.35;
    cursor: pointer;
  }

  .ekskul-update-class-item:hover {
    border-color: #b9c8ef;
    background: #f8fbff;
  }

  .ekskul-update-class-item input {
    width: 15px;
    height: 15px;
    margin-top: 1px;
    accent-color: #3f5bea;
    cursor: pointer;
    flex: 0 0 auto;
  }

  .ekskul-update-form-helper {
    color: #64748b;
    font-size: 12px;
    line-height: 1.55;
  }

  .ekskul-update-select-all {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    color: #3158b5;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    white-space: nowrap;
  }

  .ekskul-update-select-all input {
    width: 14px;
    height: 14px;
    accent-color: #3f5bea;
  }

  @media (max-width: 768px) {
    .ekskul-update-class-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 576px) {
    .ekskul-update-class-panel {
      padding: 12px;
    }

    .ekskul-update-class-toolbar {
      align-items: flex-start;
      flex-direction: column;
    }

    .ekskul-update-class-grid {
      grid-template-columns: 1fr;
    }
  }
`;

const getKelasId = (kelas) =>
  kelas?.id || kelas?.ysb_kelas_id || kelas?.kelas_id || "";

const getKelasName = (kelas) =>
  kelas?.name_kelas || kelas?.kelas_name || kelas?.name || "-";

const getGradeIdFromKelas = (kelas) =>
  kelas?.ysb_grade_level_id ||
  kelas?.grade_level_id ||
  kelas?.gradeLevelId ||
  kelas?.grade_level?.id ||
  kelas?.ysb_grade_level?.id ||
  "";

const getSchoolIdFromKelas = (kelas) =>
  kelas?.ysb_school_id ||
  kelas?.school_id ||
  kelas?.school_code ||
  kelas?.school?.id ||
  "";

const getBranchIdFromKelas = (kelas) =>
  kelas?.ysb_branch_id ||
  kelas?.branch_id ||
  kelas?.branch_code ||
  kelas?.branch ||
  "";

const isConfigKelasActive = (kelas) => {
  if (kelas?.state_form !== undefined && kelas?.state_form !== null) {
    return String(kelas.state_form) === "1";
  }

  const semesters = Array.isArray(kelas?.semesters) ? kelas.semesters : [];

  if (semesters.length > 0) {
    return semesters.some((semester) => String(semester?.state_form) === "1");
  }

  return true;
};

const toTitleCase = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

export default function ModalUpdateEkskul(props) {
  const token = localStorage.getItem("token");
  const storageLevel = localStorage.getItem("level") || "developer";

  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [kelasList, setKelasList] = useState([]);
  const [selectedKelasIds, setSelectedKelasIds] = useState([]);

  const fetchParams = useMemo(
    () => ({
      headers: {
        Authorization: `${token}`,
        "Content-Type": "application/json",
      },
    }),
    [token]
  );

  const selectedUserId = (
    localStorage.getItem("id_admin") ||
    localStorage.getItem("user_id") ||
    localStorage.getItem("id") ||
    ""
  ).replace(/[|&;$%@"<>()+,]/g, "");

  const selectedEkskulId =
    props?.rowData?.ysb_matpel_id ||
    props?.rowData?.matpel_id ||
    props?.rowData?.id ||
    "";

  const getSelectedKelasFromRow = () => {
    const ids = [];

    (props?.rowData?.grade_levels || []).forEach((gradeLevel) => {
      const gradeLevelId =
        gradeLevel?.ysb_grade_level_id || gradeLevel?.id || "";

      if (String(gradeLevelId) !== String(props?.gradeLevelId)) return;

      (gradeLevel?.kelas || []).forEach((kelas) => {
        const kelasId =
          kelas?.ysb_kelas_id || kelas?.kelas_id || kelas?.id || "";
        const schoolId = getSchoolIdFromKelas(kelas);

        if (!kelasId) return;
        if (
          schoolId &&
          String(schoolId) !== String(props?.schoolId)
        ) {
          return;
        }

        if (isConfigKelasActive(kelas)) ids.push(kelasId);
      });
    });

    return Array.from(new Set(ids));
  };

  useEffect(() => {
    if (!props?.show) return undefined;

    let isActive = true;
    setSelectedKelasIds(getSelectedKelasFromRow());
    setKelasList([]);

    const fetchKelas = async () => {
      if (!props?.branchId || !props?.schoolId || !props?.gradeLevelId) {
        return;
      }

      setLoading(true);

      try {
        const response = await APIMS.get(
          `/api/kelas?page=1&limit=10000&ascending=1&branch=${props.branchId}&level=${storageLevel}`,
          fetchParams
        );

        if (!isActive) return;

        const rows = Array.isArray(response?.data?.data)
          ? response.data.data
          : [];

        setKelasList(
          rows
            .filter((kelas) => {
              const branchId = getBranchIdFromKelas(kelas);
              const schoolId = getSchoolIdFromKelas(kelas);
              const gradeLevelId = getGradeIdFromKelas(kelas);

              const branchMatch =
                !branchId || String(branchId) === String(props.branchId);
              const schoolMatch =
                !schoolId || String(schoolId) === String(props.schoolId);
              const gradeMatch =
                String(gradeLevelId) === String(props.gradeLevelId);

              return branchMatch && schoolMatch && gradeMatch;
            })
            .sort((a, b) =>
              String(getKelasName(a)).localeCompare(
                String(getKelasName(b)),
                "id",
                { numeric: true, sensitivity: "base" }
              )
            )
        );
      } catch (error) {
        if (!isActive) return;

        console.error("Gagal mengambil data kelas untuk update ekskul:", error);
        setKelasList([]);

        ToastError.fire({
          icon: "error",
          title:
            error?.response?.data?.message || "Gagal mengambil data kelas",
        });
      } finally {
        if (isActive) setLoading(false);
      }
    };

    fetchKelas();

    return () => {
      isActive = false;
    };
  }, [
    props?.show,
    props?.rowData,
    props?.branchId,
    props?.schoolId,
    props?.gradeLevelId,
    storageLevel,
    fetchParams,
  ]);

  const allChecked =
    kelasList.length > 0 &&
    kelasList.every((kelas) => {
      const kelasId = getKelasId(kelas);
      return selectedKelasIds.some(
        (selectedId) => String(selectedId) === String(kelasId)
      );
    });

  const handleCheckKelas = (kelasId) => {
    if (!kelasId) return;

    setSelectedKelasIds((prev) => {
      const isChecked = prev.some(
        (selectedId) => String(selectedId) === String(kelasId)
      );

      return isChecked
        ? prev.filter((selectedId) => String(selectedId) !== String(kelasId))
        : [...prev, kelasId];
    });
  };

  const handleCheckAll = () => {
    setSelectedKelasIds(
      allChecked ? [] : kelasList.map((kelas) => getKelasId(kelas)).filter(Boolean)
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (
      !props?.academicYearId ||
      !props?.branchId ||
      !props?.schoolId ||
      !props?.gradeLevelId ||
      !selectedEkskulId
    ) {
      ToastError.fire({
        icon: "error",
        title: "Data konfigurasi ekstrakurikuler belum lengkap",
      });
      return;
    }

    if (selectedKelasIds.length === 0) {
      ToastError.fire({
        icon: "error",
        title: "Pilih minimal satu kelas",
      });
      return;
    }

    if (!selectedUserId) {
      ToastError.fire({
        icon: "error",
        title: "User ID belum tersedia. Silakan login ulang.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        ysb_academic_year_id: props.academicYearId,
        ysb_branch_id: props.branchId,
        ysb_school_id: props.schoolId,
        ysb_grade_level_id: props.gradeLevelId,
        ysb_matpel_id: selectedEkskulId,
        ysb_kelas_id: selectedKelasIds,
        user_id: selectedUserId,
      };

      const response = await APISD.put(
        "/api/ekstrakurikuler-configs/update",
        payload,
        fetchParams
      );

      const isSuccess =
        response?.status === 200 ||
        response?.status === 201 ||
        response?.data?.status === 200 ||
        response?.data?.status === 201;

      if (!isSuccess || response?.data?.error === true) {
        throw new Error(
          response?.data?.message ||
            "Gagal memperbarui konfigurasi ekstrakurikuler"
        );
      }

      ToastSuccess.fire({
        icon: "success",
        title:
          response?.data?.message ||
          "Konfigurasi ekstrakurikuler berhasil diperbarui",
      });

      props?.onHide?.();
      await props?.GetResponseData?.();
    } catch (error) {
      console.error("Gagal memperbarui konfigurasi ekstrakurikuler:", error);

      ToastError.fire({
        icon: "error",
        title:
          error?.response?.data?.message ||
          error?.message ||
          "Gagal memperbarui konfigurasi ekstrakurikuler",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      {...props}
      size="lg"
      centered
      backdrop="static"
      keyboard={false}
      dialogClassName="module-modal-dialog module-modal-dialog-lg"
      contentClassName="module-modal-content"
    >
      <style>{css}</style>

      <Form onSubmit={handleSubmit}>
        <div className="module-modal-header">
          <div>
            <h5 className="module-modal-title">Update Ekskul Kelas</h5>
            
          </div>

          <button
            type="button"
            className="module-modal-close"
            onClick={props.onHide}
            disabled={isSubmitting}
            aria-label="Tutup modal"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <Modal.Body className="module-modal-body mt-1 mx-1">
          <div className="module-form-grid">
            <div className="module-form-group">
              <label className="module-form-label">Tahun Ajaran</label>
              <select
                className="module-form-select"
                value={props?.academicYearId || ""}
                disabled
              >
                <option value={props?.academicYearId || ""}>
                  {props?.academicYearName || "Tahun ajaran belum dipilih"}
                </option>
              </select>
            </div>

            <div className="module-form-group">
              <label className="module-form-label">Cabang</label>
              <select
                className="module-form-select"
                value={props?.branchId || ""}
                disabled
              >
                <option value={props?.branchId || ""}>
                  {props?.branchName || props?.branchId || "Cabang belum dipilih"}
                </option>
              </select>
            </div>

            <div className="module-form-group">
              <label className="module-form-label">Sekolah</label>
              <select
                className="module-form-select"
                value={props?.schoolId || ""}
                disabled
              >
                <option value={props?.schoolId || ""}>
                  {props?.schoolName || props?.schoolId || "Sekolah belum dipilih"}
                </option>
              </select>
            </div>

            <div className="module-form-group">
              <label className="module-form-label">Tingkatan</label>
              <select
                className="module-form-select"
                value={props?.gradeLevelId || ""}
                disabled
              >
                <option value={props?.gradeLevelId || ""}>
                  {props?.gradeLevelName
                    ? `Tingkatan ${props.gradeLevelName}`
                    : "Tingkatan belum dipilih"}
                </option>
              </select>
            </div>

            <div className="module-form-group module-form-span-2">
              <label className="module-form-label">Ekstrakurikuler</label>
              <select
                className="module-form-select"
                value={selectedEkskulId}
                disabled
              >
                <option value={selectedEkskulId}>
                  {props?.rowData?.name_matpel || props?.nameUpdate || "-"}
                </option>
              </select>
            </div>

            <div className="module-form-group module-form-span-2">
              <label className="module-form-label">
                Kelas <span>*</span>
              </label>

              <div className="ekskul-update-class-panel">
                {loading ? (
                  <div className="ekskul-update-form-helper">
                    Memuat data kelas...
                  </div>
                ) : kelasList.length === 0 ? (
                  <div className="ekskul-update-form-helper">
                    Kelas tidak ditemukan pada sekolah dan tingkatan ini.
                  </div>
                ) : (
                  <>
                    <div className="ekskul-update-class-toolbar">
                      <div>
                        <strong>Pilih kelas tujuan</strong>
                        <br />
                        <span>{kelasList.length} kelas tersedia</span>
                      </div>

                      <label className="ekskul-update-select-all">
                        <input
                          type="checkbox"
                          checked={allChecked}
                          disabled={isSubmitting}
                          onChange={handleCheckAll}
                        />
                        Pilih semua
                      </label>
                    </div>

                    <div className="ekskul-update-class-grid">
                      {kelasList.map((kelas, index) => {
                        const kelasId = getKelasId(kelas);
                        const checked = selectedKelasIds.some(
                          (selectedId) =>
                            String(selectedId) === String(kelasId)
                        );

                        return (
                          <label
                            key={kelasId || index}
                            className="ekskul-update-class-item"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={isSubmitting}
                              onChange={() => handleCheckKelas(kelasId)}
                            />
                            <span>
                              {props?.gradeLevelName || ""}{" "}
                              {toTitleCase(getKelasName(kelas))}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>

        <div className="module-modal-footer">
          <button
            type="button"
            className="module-modal-cancel"
            onClick={props.onHide}
            disabled={isSubmitting}
          >
            Batal
          </button>

          <button
            type="submit"
            className="module-modal-submit"
            disabled={loading || isSubmitting || selectedKelasIds.length === 0}
          >
            {isSubmitting && <span className="module-button-spinner" />}
            {isSubmitting ? "Memperbarui..." : "Perbarui"}
          </button>
        </div>
      </Form>
    </Modal>
  );
}
